/**
 * WorkspaceCard Component
 *
 * Professional card displaying workspace (case/tenant) information.
 * "Command Docket" aesthetic - editorial legal meets mission control.
 * Features color avatar for visual continuity with navigation.
 */

import { Link } from 'react-router-dom';
import {
  makeStyles,
  tokens,
  Text,
  mergeClasses,
  shorthands,
} from '@fluentui/react-components';
import {
  Clock20Regular,
  Document20Regular,
  CalendarLtr20Regular,
  Person20Regular,
} from '@fluentui/react-icons';
import type { WorkspaceDetail, WorkspaceType, WorkspacePhase } from '../../types/workspace';
import { getWorkspaceAbbreviation } from '../../utils/workspaceColors';

const useStyles = makeStyles({
  '@keyframes fadeInUp': {
    from: {
      opacity: '0',
      transform: 'translateY(16px)',
    },
    to: {
      opacity: '1',
      transform: 'translateY(0)',
    },
  },
  card: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    gap: '20px',
    backgroundColor: '#ffffff',
    ...shorthands.borderRadius('12px'),
    ...shorthands.border('1px', 'solid', 'rgba(0, 0, 0, 0.08)'),
    ...shorthands.padding('20px', '24px'),
    transitionProperty: 'border-color, box-shadow, transform',
    transitionDuration: '0.2s',
    transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'pointer',
    textDecoration: 'none',
    color: 'inherit',
    ...shorthands.overflow('hidden'),
    // Entrance animation
    animationName: 'fadeInUp',
    animationDuration: '0.4s',
    animationTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
    animationFillMode: 'both',

    '&:hover': {
      ...shorthands.borderColor('rgba(0, 0, 0, 0.15)'),
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)',
      transform: 'translateY(-2px)',
    },
  },

  // Color avatar - matches navigation style
  avatar: {
    width: '44px',
    height: '44px',
    ...shorthands.borderRadius('10px'),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: 'white',
    fontSize: '15px',
    fontWeight: '600',
    letterSpacing: '0.02em',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.12)',
  },

  // Main content area
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    minWidth: 0,
  },

  // Header row: workspace number, name, phase badge
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '16px',
    marginBottom: '16px',
  },

  headerLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
    flex: 1,
  },

  headerRight: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '24px',
    flexShrink: 0,
  },

  headerItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    minWidth: '100px',
  },

  headerLabel: {
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'rgba(0, 0, 0, 0.4)',
  },

  createdDate: {
    fontSize: '13px',
    fontWeight: '500',
    color: 'rgba(0, 0, 0, 0.85)',
  },

  workspaceNumber: {
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'rgba(0, 0, 0, 0.45)',
  },

  workspaceName: {
    fontSize: '18px',
    fontWeight: '600',
    color: 'rgba(0, 0, 0, 0.9)',
    lineHeight: '1.3',
    letterSpacing: '-0.01em',
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  // Phase badge
  phaseBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    ...shorthands.padding('4px', '10px'),
    ...shorthands.borderRadius('6px'),
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    flexShrink: 0,
  },

  phaseIntake: {
    backgroundColor: 'rgba(99, 102, 241, 0.12)',
    color: '#4F46E5',
  },
  phaseDiscovery: {
    backgroundColor: 'rgba(59, 130, 246, 0.12)',
    color: '#2563EB',
  },
  phaseAnalysis: {
    backgroundColor: 'rgba(245, 158, 11, 0.12)',
    color: '#D97706',
  },
  phaseRemediation: {
    backgroundColor: 'rgba(16, 185, 129, 0.12)',
    color: '#059669',
  },
  phaseClosing: {
    backgroundColor: 'rgba(107, 114, 128, 0.12)',
    color: '#4B5563',
  },
  phaseClosed: {
    backgroundColor: 'rgba(107, 114, 128, 0.08)',
    color: '#6B7280',
  },

  // Meta row: type, lead, role
  metaRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '48px',
    alignItems: 'center',
    marginBottom: '8px',
  },

  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: 'rgba(0, 0, 0, 0.85)',
  },

  metaIcon: {
    color: 'rgba(0, 0, 0, 0.4)',
    flexShrink: 0,
  },

  metaLabel: {
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'rgba(0, 0, 0, 0.4)',
    marginRight: '4px',
  },

  // Stats row: documents, last activity, deadline
  statsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '48px',
    alignItems: 'center',
    ...shorthands.padding('16px', 0, 0, 0),
    ...shorthands.borderTop('1px', 'solid', 'rgba(0, 0, 0, 0.06)'),
    marginTop: '8px',
  },

  stat: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: 'rgba(0, 0, 0, 0.85)',
  },

  statIcon: {
    color: 'rgba(0, 0, 0, 0.35)',
  },

  statLabel: {
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: 'rgba(0, 0, 0, 0.4)',
  },

  statHighlight: {
    color: tokens.colorBrandForeground1,
    fontWeight: '600',
  },

  deadlineUrgent: {
    color: '#DC2626',
    fontWeight: '600',
  },

  // Activity indicator
  activityIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },

  activityDot: {
    width: '8px',
    height: '8px',
    ...shorthands.borderRadius('50%'),
    backgroundColor: '#10B981',
  },

  activityText: {
    fontSize: '12px',
    color: 'rgba(0, 0, 0, 0.5)',
  },
});

interface WorkspaceCardProps {
  workspace: WorkspaceDetail;
  style?: React.CSSProperties;
}

/**
 * Format workspace type for display
 */
function formatWorkspaceType(type: WorkspaceType): string {
  const typeLabels: Record<WorkspaceType, string> = {
    data_breach: 'Data Breach',
    litigation: 'Litigation',
    investigation: 'Investigation',
    compliance: 'Compliance',
    advisory: 'Advisory',
  };
  return typeLabels[type];
}

/**
 * Get phase badge style
 */
function getPhaseStyle(phase: WorkspacePhase, styles: ReturnType<typeof useStyles>): string {
  const phaseStyles: Record<WorkspacePhase, string> = {
    intake: styles.phaseIntake,
    discovery: styles.phaseDiscovery,
    analysis: styles.phaseAnalysis,
    remediation: styles.phaseRemediation,
    closing: styles.phaseClosing,
    closed: styles.phaseClosed,
  };
  return phaseStyles[phase];
}

/**
 * Format phase for display
 */
function formatPhase(phase: WorkspacePhase): string {
  return phase.charAt(0).toUpperCase() + phase.slice(1);
}

/**
 * Format relative time
 */
function formatRelativeTime(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Format deadline with urgency indication
 */
function formatDeadline(isoString: string): { text: string; isUrgent: boolean } {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));

  if (diffDays < 0) {
    return { text: 'Overdue', isUrgent: true };
  }
  if (diffDays === 0) {
    return { text: 'Today', isUrgent: true };
  }
  if (diffDays === 1) {
    return { text: 'Tomorrow', isUrgent: true };
  }
  if (diffDays <= 3) {
    return { text: `${diffDays} days`, isUrgent: true };
  }
  if (diffDays <= 7) {
    return { text: `${diffDays} days`, isUrgent: false };
  }
  return {
    text: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    isUrgent: false,
  };
}

export function WorkspaceCard({ workspace, style }: WorkspaceCardProps) {
  const styles = useStyles();
  const abbreviation = getWorkspaceAbbreviation(workspace.name);
  const deadlineInfo = workspace.nextDeadline ? formatDeadline(workspace.nextDeadline) : null;

  return (
    <Link to={`/app/workspaces/${workspace.id}`} className={styles.card} style={style} data-testid="workspace-card">
      {/* Color avatar */}
      <div
        className={styles.avatar}
        style={{ backgroundColor: workspace.color }}
        data-testid={`workspace-avatar-${workspace.id}`}
      >
        {abbreviation}
      </div>

      {/* Main content */}
      <div className={styles.content}>
        {/* Header: Number, Name, Created, Phase */}
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <Text className={styles.workspaceNumber}>{workspace.workspaceNumber}</Text>
            <Text className={styles.workspaceName}>{workspace.name}</Text>
          </div>
          <div className={styles.headerRight}>
            <span className={styles.headerItem}>
              <span className={styles.headerLabel}>Created</span>
              <span className={styles.createdDate}>
                {new Date(workspace.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            </span>
            <span className={styles.headerItem}>
              <span className={styles.headerLabel}>Status</span>
              <span className={mergeClasses(styles.phaseBadge, getPhaseStyle(workspace.phase, styles))}>
                {formatPhase(workspace.phase)}
              </span>
            </span>
          </div>
        </header>

        {/* Meta: Type, Lead, Role */}
        <div className={styles.metaRow}>
          <span className={styles.metaItem}>
            <span className={styles.metaLabel}>Type</span>
            {formatWorkspaceType(workspace.workspaceType)}
          </span>
          <span className={styles.metaItem}>
            <Person20Regular className={styles.metaIcon} />
            <span className={styles.metaLabel}>Lead</span>
            {workspace.leadName}
          </span>
          <span className={styles.metaItem}>
            <span className={styles.metaLabel}>Your Role</span>
            {workspace.role}
          </span>
        </div>

        {/* Stats: Documents, Last Activity, Deadline */}
        <div className={styles.statsRow}>
          <span className={styles.stat}>
            <Document20Regular className={styles.statIcon} />
            <span className={styles.statLabel}>Docs</span>
            {workspace.documentCount.toLocaleString()}
          </span>
          <span className={styles.stat}>
            <Clock20Regular className={styles.statIcon} />
            <span className={styles.statLabel}>Activity</span>
            {formatRelativeTime(workspace.lastActivityAt)}
          </span>
          {deadlineInfo && (
            <span className={mergeClasses(styles.stat, deadlineInfo.isUrgent && styles.deadlineUrgent)}>
              <CalendarLtr20Regular className={mergeClasses(styles.statIcon, deadlineInfo.isUrgent && styles.deadlineUrgent)} />
              <span className={styles.statLabel}>Due</span>
              {deadlineInfo.text}
            </span>
          )}
          {workspace.unreadCount > 0 && (
            <span className={mergeClasses(styles.stat, styles.statHighlight)}>
              <span className={styles.statLabel}>New Files</span>
              {workspace.unreadCount}
            </span>
          )}
          {workspace.newFilesCount > 0 && (
            <span className={mergeClasses(styles.stat, styles.statHighlight)}>
              <span className={styles.statLabel}>Changed Files</span>
              {workspace.newFilesCount}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
